import React from 'react';
import {
  Unlink, Gavel, ShieldOff, Wrench, FileX, Ban, AlertTriangle,
} from 'lucide-react';

const ISSUES = [
  {
    icon: Unlink,
    title: 'Invalid Break Clauses',
    desc: 'A poorly drafted break clause can be completely unenforceable, trapping you in a lease you can\'t exit.',
    consequence: 'Months or years of wasted rent payments with no legal way out',
  },
  {
    icon: Gavel,
    title: 'Renters Rights Bill Violations',
    desc: 'Clauses that violated the 2024 Renters Rights Bill are now illegal and unenforceable.',
    consequence: 'Agreement becomes void; potential fines and legal liability',
  },
  {
    icon: ShieldOff,
    title: 'Inadequate Deposit Protection',
    desc: 'Missing or incorrect deposit protection information can void your claim to the deposit.',
    consequence: 'Unable to retain deposit even if tenant causes damage',
  },
  {
    icon: Wrench,
    title: 'Ambiguous Maintenance Responsibilities',
    desc: 'Unclear maintenance clause boundaries lead to disputes over who pays for repairs.',
    consequence: 'Costly legal disputes and emergency repair bills you can\'t recover',
  },
  {
    icon: FileX,
    title: 'Missing or Invalid Notices',
    desc: 'Failure to provide required statutory notices invalidates the entire agreement.',
    consequence: 'Tenant can live rent-free; eviction impossible without serving proper notices again',
  },
  {
    icon: Ban,
    title: 'Unenforceable Penalty Clauses',
    desc: 'Overly harsh penalties for minor violations become void under UK law.',
    consequence: 'Penalty clauses stripped out by courts; agreement loses protective provisions',
  },
];

export function ComplianceIssues() {
  return (
    <section className="py-20 sm:py-28 bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-16 animate-on-scroll">
          <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-4 py-1.5 mb-6">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-700">Critical Compliance Issues</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Avoid These Costly Compliance Mistakes
          </h2>
          <p className="text-lg text-gray-500 max-w-xl mx-auto">
            Common issues we find in 80% of reviewed agreements
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {ISSUES.map((issue, i) => {
            const Icon = issue.icon;
            return (
              <div
                key={issue.title}
                className={`animate-on-scroll delay-${(i % 3) * 100 + 100} bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-elevated transition-shadow duration-300`}
              >
                <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{issue.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed mb-4">{issue.desc}</p>
                <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                  <p className="text-xs font-semibold text-red-800 uppercase tracking-wider mb-1">Consequence</p>
                  <p className="text-sm text-red-700 leading-relaxed">{issue.consequence}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="animate-on-scroll bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 sm:p-10 text-center">
          <h3 className="text-xl font-bold text-white mb-3">The Real Cost of Getting It Wrong</h3>
          <p className="text-gray-300 text-sm max-w-2xl mx-auto leading-relaxed mb-6">
            A single overlooked clause can expose you to thousands in fines, legal fees, and unenforceable agreements.
            Our review catches these before they become costly problems.
          </p>
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-xl px-6 py-3">
            <span className="text-white/70 text-sm">Average legal cost to fix after the fact:</span>
            <span className="text-white font-bold text-lg">&pound;2,000 - &pound;5,000+</span>
          </div>
        </div>
      </div>
    </section>
  );
}
